import {
  Box,
  Button,
  CircularProgress,
  Grid2 as Grid,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { CustomIcon, Icons, NoData, SearchBar } from "components/common";
import SelectDropdown from "../../../../components/common/SelectDropdown/SelectDropdown";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import MacroAdjustmentRow from "../MacroAdjustmentRow/MacroAdjustmentRow";
import {
  CountryViewModel,
  MacroScenarioAdjustmentOverview,
  ViewMacroScenarioDto,
} from "orval/generated/models";
import { deepClone } from "utils";
import { debounce } from "lodash";
import {
  getGetAllScenariosMacroScenarioIdAdjustmentsGetQueryKey,
  useCalculateTonnesOfCo2PerAdjustmentScenariosMacroScenarioIdAdjustmentsCalculateTonnesOfCo2Put,
  useGetAllScenariosMacroScenarioIdAdjustmentsGet,
  useReorderMacroAdjustmentsScenariosMacroScenarioIdAdjustmentsOrderPut,
} from "orval/generated/endpoint";
import { useSnackbar } from "components/common/Notification/showSnackbar";
import LoadingList from "components/common/LoadingList/LoadingList";
import { useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
// import { useModal } from "components/common/Modal";
// import ConfirmationModal from "components/common/ConfirmationModal/ConfirmationModal";

export type MacroAdjustmentsPageTableProps = {
  scenario: ViewMacroScenarioDto;
};

const MacroAdjustmentsPageTable = ({
  scenario,
}: MacroAdjustmentsPageTableProps) => {
  const { t } = useTranslation("macro");
  const showSnackbar = useSnackbar();
  const { palette } = useTheme();
  const queryClient = useQueryClient();
  // const { openModal, closeModal } = useModal();

  const {
    data: adjustments,
    isPending: isFetchingAdjustments,
    isError: errorFetchingAdjustments,
  } = useGetAllScenariosMacroScenarioIdAdjustmentsGet(scenario.id as number);

  const { mutateAsync: changeOrder, isPending: changeOrderIsPending } =
    useReorderMacroAdjustmentsScenariosMacroScenarioIdAdjustmentsOrderPut({
      mutation: {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: getGetAllScenariosMacroScenarioIdAdjustmentsGetQueryKey(
              scenario.id,
            ),
          });

          setChangeSettingsIsEnabled(false);
          showSnackbar(
            t("macro:adjustmentsPage.updateMacroAdjustmentSettings"),
            "success",
          );
        },
        onError: (error: AxiosError) => {
          showSnackbar(
            t("macro:adjustmentsPage.failedToUpdateMacroAdjustmentSettings"),
            "error",
          );
          console.warn(error);
          return error;
        },
      },
    });

  const {
    mutateAsync: calculateTonnesPerAdjustments,
    isPending: calculateTonnesPerAdjustmentsIsPending,
  } =
    useCalculateTonnesOfCo2PerAdjustmentScenariosMacroScenarioIdAdjustmentsCalculateTonnesOfCo2Put(
      {
        mutation: {
          onSuccess: async () => {
            await queryClient.invalidateQueries({
              queryKey: getGetAllScenariosMacroScenarioIdAdjustmentsGetQueryKey(
                scenario.id,
              ),
            });

            showSnackbar(
              t("macro:adjustmentsPage.calculateTonnesPerAdjustmentsSuccess"),
              "success",
            );

            //TODO: This modal will be needed if we decide to do the calculation in the background rather than waiting for a long time

            // openModal(
            //   <ConfirmationModal
            //     title={t("macro:adjustmentsPage.calculateTonnesModal.title")}
            //     description={t(
            //       "macro:adjustmentsPage.calculateTonnesModal.description",
            //     )}
            //     actionTitle={t(
            //       "macro:adjustmentsPage.calculateTonnesModal.actionTitle",
            //     )}
            //     confirmAction={() => {
            //       closeModal();
            //     }}
            //   />,
            // );
          },
          onError: (error: AxiosError) => {
            showSnackbar(
              t("macro:adjustmentsPage.failedTocalculateTonnesPerAdjustments"),
              "error",
            );
            console.warn(error);
            return error;
          },
        },
      },
    );

  const [sortedData, setSortedData] = useState<
    MacroScenarioAdjustmentOverview[]
  >([]);

  const [changeSettingsIsEnabled, setChangeSettingsIsEnabled] =
    useState<boolean>(false);

  const handleChangeOrder = useCallback(
    (move: "up" | "down", adjustmentId: number) => {
      const targetAdjustment = sortedData.find(
        (adjustment) => adjustment.id === adjustmentId,
      ) as MacroScenarioAdjustmentOverview;

      const targetAdjustmentNewPostion =
        move === "up"
          ? targetAdjustment.position - 1
          : targetAdjustment.position + 1;

      setSortedData((prev) =>
        prev
          ?.map((adjustment) => ({
            ...adjustment,
            position: (adjustment.id === adjustmentId
              ? targetAdjustmentNewPostion
              : adjustment.position === targetAdjustmentNewPostion
                ? targetAdjustment.position
                : adjustment.position) as number,
          }))
          .sort(
            (
              a: MacroScenarioAdjustmentOverview,
              b: MacroScenarioAdjustmentOverview,
            ) => a.position - b.position,
          ),
      );
    },
    [sortedData],
  );

  const handleToggle = useCallback((enable: boolean, adjustmentId: number) => {
    setSortedData((prev) =>
      prev?.map((adjustment) => ({
        ...adjustment,
        is_enabled:
          adjustmentId === adjustment.id ? enable : adjustment.is_enabled,
      })),
    );
  }, []);

  const handleChangeOrderSave = useCallback(async () => {
    try {
      await changeOrder({
        scenarioId: scenario.id,
        data: sortedData.map(({ position, id, is_enabled }) => ({
          adjustment_id: id,
          position,
          is_enabled,
        })),
      });
    } catch (e) {
      console.log(e);
    }
  }, [changeOrder, scenario.id, sortedData]);

  const [searchValue, setSearchValue] = useState<string>("");

  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);

  const debouncedHandleSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearchValue(value);
      }, 300),
    [],
  );

  const handleCountriesTypeChange = useCallback(
    (newSelectedCountries: string[]) => {
      setSelectedCountries(newSelectedCountries);
    },
    [],
  );

  useEffect(() => {
    if (adjustments) {
      if (adjustments.length) {
        let sortedItems: MacroScenarioAdjustmentOverview[] = [];
        //search
        const words = searchValue
          .replace(",", " ")
          .trim()
          .toLocaleLowerCase()
          .split(" ");
        const _adjustments: MacroScenarioAdjustmentOverview[] = deepClone(
          adjustments,
        ).sort(
          (
            a: MacroScenarioAdjustmentOverview,
            b: MacroScenarioAdjustmentOverview,
          ) => a.position - b.position,
        );

        if (words.length) {
          _adjustments.forEach((adjustment) => {
            words.forEach((word) => {
              if (
                `${adjustment.name}${adjustment.pillar}${adjustment.input_area}`
                  .toLocaleLowerCase()
                  .includes(word)
              )
                if (
                  !sortedItems.find(
                    (_adjustment) => _adjustment.id === adjustment.id,
                  )
                ) {
                  sortedItems.push(adjustment);
                }
            });
          });
        } else {
          sortedItems = _adjustments;
        }

        //filters
        if (selectedCountries.length) {
          sortedItems = sortedItems.filter((adjustment) =>
            adjustment.countries.some((country: string) =>
              selectedCountries.includes(
                scenario.inputs?.countries?.find(
                  (_country) => _country.country === country,
                )?.display_name as string,
              ),
            ),
          );
        }

        setSortedData(sortedItems);
      } else {
        setSortedData([]);
      }
    }
  }, [adjustments, scenario.inputs?.countries, searchValue, selectedCountries]);

  useEffect(() => {
    if (errorFetchingAdjustments) {
      showSnackbar(t("adjustmentsPage.failedToFetchAdjustments"), "error");
    }
  }, [errorFetchingAdjustments, showSnackbar, t]);

  const enabledAdjustmentsCount = useMemo(
    () => adjustments?.filter((adjustment) => adjustment.is_enabled).length,
    [adjustments],
  );

  return (
    <Box>
      <Grid container spacing={1} alignItems={"center"}>
        <Grid size={{ xs: 12, lg: 7 }}>
          <SearchBar
            handleSearch={debouncedHandleSearch}
            disabled={!adjustments?.length}
            placeHolder={t("macro:searchAdjustments")}
          />
        </Grid>

        <Grid
          size={{ xs: 4, lg: 2 }}
          mt={"4px"}
          data-testid="coutries-dropdown"
        >
          {scenario?.inputs?.countries && (
            <SelectDropdown
              listItems={scenario.inputs?.countries
                ?.filter((country) => {
                  return (
                    adjustments &&
                    adjustments.some((adjustment) =>
                      adjustment.countries.includes(country.country),
                    )
                  );
                })
                .map((country) => country.display_name)}
              savedSelectedItems={selectedCountries}
              onSave={handleCountriesTypeChange}
              title={t("adjustmentsPage.countriesDropdownTitle")}
              compact
              showSearchBar={false}
              selectAll={true}
              disabled={!adjustments?.length}
            ></SelectDropdown>
          )}
        </Grid>
      </Grid>
      {isFetchingAdjustments ? (
        <LoadingList />
      ) : !adjustments?.length ? (
        <Box mt={2}>
          <NoData
            label={t("adjustmentsPage.noAdjustmentsAvailableLabel")}
            label2={t("adjustmentsPage.noAdjustmentsAvailableDescription")}
          />
        </Box>
      ) : !sortedData?.length ? (
        <Box mt={2}>
          <NoData label={t("adjustmentsPage.noAdjustmentsMatch")} />
        </Box>
      ) : (
        <>
          <Box
            display="flex"
            alignItems="center"
            width="100%"
            justifyContent="space-between"
            py={1}
          >
            <Box display="flex" alignItems="center">
              <Typography
                variant="h3"
                sx={{ color: palette.primary.main, fontWeight: 500 }}
              >
                {enabledAdjustmentsCount}/{adjustments.length}{" "}
                {t("macro:adjustmentsPage.enabled")}
              </Typography>
              <Tooltip
                title={`${t("macro:adjustmentsPage.enabledAdjustmentsToolTipPartOne")} ${enabledAdjustmentsCount} ${t("macro:adjustmentsPage.enabledAdjustmentsToolTipPartTwo")} ${scenario.inputs?.years?.target_year} ${t("macro:adjustmentsPage.enabledAdjustmentsToolTipPartThree")}`}
              >
                <IconButton>
                  <CustomIcon
                    name={Icons.INFORMATION}
                    fill={palette.primary.main}
                    width={25}
                    height={25}
                  />
                </IconButton>
              </Tooltip>
            </Box>

            <Box display="flex" alignItems="center">
              {adjustments.length ? (
                <Box mr={2}>
                  <Button
                    variant={"outlined"}
                    disabled={
                      calculateTonnesPerAdjustmentsIsPending ||
                      changeOrderIsPending ||
                      !adjustments.some(
                        (adjustment) => adjustment.tonnes_of_co2 === null,
                      )
                    }
                    onClick={async () =>
                      await calculateTonnesPerAdjustments({
                        scenarioId: scenario.id,
                      })
                    }
                  >
                    {t("macro:adjustmentsPage.calculateSavingPerAdjustment")}
                    {calculateTonnesPerAdjustmentsIsPending && (
                      <CircularProgress size={10} sx={{ ml: 1 }} />
                    )}
                  </Button>
                </Box>
              ) : null}

              {!!adjustments.length && (
                <Button
                  variant={changeSettingsIsEnabled ? "contained" : "outlined"}
                  disabled={changeOrderIsPending}
                  onClick={
                    changeSettingsIsEnabled
                      ? handleChangeOrderSave
                      : () => {
                          setSearchValue("");
                          setSelectedCountries([]);
                          setChangeSettingsIsEnabled(true);
                        }
                  }
                >
                  {changeSettingsIsEnabled
                    ? t("macro:adjustmentsPage.saveSettings")
                    : t("macro:adjustmentsPage.changeSettings")}
                  {changeOrderIsPending && (
                    <CircularProgress size={10} sx={{ ml: 1 }} />
                  )}
                </Button>
              )}
            </Box>
          </Box>
          <Grid
            container
            direction="column"
            justifyContent="center"
            spacing={2}
            mb={2}
          >
            {sortedData.map((adjustment) => (
              <Grid maxWidth="100% !important" size={12} key={adjustment.id}>
                <MacroAdjustmentRow
                  adjustmentDetails={adjustment}
                  scenarioId={scenario.id as number}
                  scenarioCountries={
                    scenario.inputs?.countries as CountryViewModel[]
                  }
                  isLastRow={adjustment.position === sortedData.length}
                  changeOrderIsPending={changeOrderIsPending}
                  changeSettingsIsEnabled={changeSettingsIsEnabled}
                  handleChangeOrder={handleChangeOrder}
                  handleToggle={handleToggle}
                />
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Box>
  );
};
export default MacroAdjustmentsPageTable;
